import React, {useCallback, useEffect, useRef, useState} from 'react'
import {Input, InputGroup, InputGroupText} from 'reactstrap'
import {useShapesTemplateList} from '../../hooks/useShapesTemplateList'
import {useDraftShapes} from '../../hooks/useDraftShapes'
import {usePagination} from '../../hooks/usePagination'
import {restoreDraftImages} from '../../utils/restoreDraftImages'
import Icon from '../../../../common/Icon'
import TrashIcon from '../../../../common/icons/TrashIcon'
import DeleteShapeConfirmModal from './DeleteShapeConfirmModal'
import {SHAPE_PATHS} from '../../../../../helpers/shapes/shapePaths'
import {addNode} from '../../../editorActions'
import {placeNodeAtViewportCenter} from '../../../utils/placeNodeAtViewportCenter'

const BASIC_SHAPES = Object.entries(SHAPE_PATHS).map(([shapeType, def]) => ({
    shapeType,
    data: def.data,
    rawWidth: def.rawWidth,
    rawHeight: def.rawHeight,
    label: shapeType
        .split('-')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' '),
}))

const BASIC_SHAPE_LIMIT = 14

const ShapesPanel = ({runtime, onSelectDraft, onSelectShape}) => {
    const [searchQuery, setSearchQuery] = useState('')
    const searchTimeoutRef = useRef(null)
    const [deleteConfirm, setDeleteConfirm] = useState(null)

    const {items, total, page, limit, loading, error, loadPage, setFilters} = useShapesTemplateList({limit: 12})

    const {drafts, loading: draftsLoading, deleteDraft} = useDraftShapes()
    const draftLimit = 12

    const basicPagination = usePagination({
        totalItems: BASIC_SHAPES.length,
        limit: BASIC_SHAPE_LIMIT,
    })
    const draftPagination = usePagination({
        totalItems: drafts.length,
        limit: draftLimit,
        resetKey: drafts.length,
    })
    const savedPagination = usePagination({
        totalItems: total,
        limit,
        page,
        onPageChange: loadPage,
    })

    const handleAddBasicShape = useCallback(
        (shapeType) => {
            if (!runtime?.dispatch) return
            const shapeDef = SHAPE_PATHS[shapeType]
            if (!shapeDef) return
            const id = Date.now().toString(36) + Math.random().toString(36).slice(2)
            const node = placeNodeAtViewportCenter(
                {
                    id,
                    type: 'svg-shape',
                    geometry: {
                        x: 100,
                        y: 100,
                        width: shapeDef.rawWidth,
                        height: shapeDef.rawHeight,
                        rotation: 0,
                    },
                    // Persist shapeType inside style so it survives serialize/persistence.
                    style: {fill: '#000000', stroke: '', strokeWidth: 0, shapeType},
                },
                runtime.getViewportCenter?.(),
            )
            runtime.dispatch(
                addNode(node),
            )
        },
        [runtime],
    )

    const handleSelectShape = useCallback(
        async (template) => {
            if (!runtime?.loadDocument) return
            const raw = template.data
            if (raw == null) return
            const doc = typeof raw === 'string' ? JSON.parse(raw) : raw
            const meta = {
                id: template._id,
                name: template.name,
                description: template.description,
            }
            await runtime.loadDocument(doc, meta)
            onSelectShape?.()
        },
        [runtime, onSelectShape],
    )

    const handleSelectDraft = useCallback(
        async (draft) => {
            if (!runtime?.loadDocument || !draft?.document) return
            await runtime.loadDocument(draft.document)
            await restoreDraftImages(runtime, draft.id)
            onSelectDraft?.(draft.id)
        },
        [runtime, onSelectDraft],
    )

    const handleSearchChange = useCallback(
        (e) => {
            const value = e.target.value
            setSearchQuery(value)
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
            searchTimeoutRef.current = setTimeout(() => {
                setFilters({name: value.trim()})
            }, 400)
        },
        [setFilters],
    )

    const handleDeleteConfirm = useCallback(() => {
        if (!deleteConfirm) return
        if (deleteConfirm.type === 'draft') {
            deleteDraft(deleteConfirm.item.id)
        }
        // Saved: no API yet; modal close is handled by DeleteShapeConfirmModal.toggle after onConfirm
    }, [deleteConfirm, deleteDraft])

    const closeDeleteModal = useCallback(() => {
        setDeleteConfirm(null)
    }, [])

    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
        }
    }, [])

    const pagedBasicShapes = BASIC_SHAPES.slice(basicPagination.startIndex, basicPagination.endIndex)
    const pagedDrafts = drafts.slice(draftPagination.startIndex, draftPagination.endIndex)

    const showDrafts = drafts.length > 0

    return (
        <section className="SidebarPanel" aria-label="Shapes">
            <header className="SidebarPanel__Header">
                <h2 className="SidebarPanel__Title">Shapes</h2>
            </header>

            <div className="SidebarPanel__Content">
                {/* Search input */}
                <div className="SidebarPanel__SearchWrap">
                    <InputGroup size="sm">
                        <InputGroupText>
                            <i className="fas fa-search" />
                        </InputGroupText>
                        <Input
                            type="text"
                            placeholder="Search shapes…"
                            value={searchQuery}
                            onChange={handleSearchChange}
                            aria-label="Search shapes"
                        />
                    </InputGroup>
                </div>

                {/* Basic Shapes block */}
                <div className="SidebarPanel__Section">
                    <h3 className="SidebarPanel__SectionTitle">Basic Shapes</h3>
                    <div className="SidebarPanel__ShapesGrid">
                        {pagedBasicShapes.map(({shapeType, data, rawWidth, rawHeight, label}) => (
                            <button
                                key={shapeType}
                                type="button"
                                className="SidebarPanel__ShapeThumb"
                                onClick={() => handleAddBasicShape(shapeType)}
                                aria-label={label}
                                title={label}
                            >
                                <svg
                                    viewBox={`0 0 ${rawWidth} ${rawHeight}`}
                                    width="60%"
                                    height="60%"
                                    aria-hidden="true"
                                    fill="currentColor"
                                >
                                    <path d={data} />
                                </svg>
                            </button>
                        ))}
                    </div>
                    {basicPagination.totalPages > 1 && (
                        <div className="SidebarPanel__Pagination">
                            <button
                                type="button"
                                className="SidebarPanel__PaginationBtn"
                                disabled={!basicPagination.canPrev}
                                onClick={basicPagination.goPrev}
                                aria-label="Previous basic shapes page"
                            >
                                Prev
                            </button>
                            <span className="SidebarPanel__PaginationLabel">
                                {basicPagination.page} / {basicPagination.totalPages}
                            </span>
                            <button
                                type="button"
                                className="SidebarPanel__PaginationBtn"
                                disabled={!basicPagination.canNext}
                                onClick={basicPagination.goNext}
                                aria-label="Next basic shapes page"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>

                {/* Draft Shapes block */}
                {draftsLoading && (
                    <div className="SidebarPanel__Section">
                        <h3 className="SidebarPanel__SectionTitle">Draft Shapes</h3>
                        <div className="SidebarPanel__ShapesGrid">
                            {Array.from({length: draftLimit}).map((_, i) => (
                                <div
                                    key={`draft-skeleton-${i}`}
                                    className="SidebarPanel__ShapeThumb SidebarPanel__ShapeThumb--skeleton"
                                />
                            ))}
                        </div>
                    </div>
                )}
                {!draftsLoading && showDrafts && (
                    <div className="SidebarPanel__Section">
                        <h3 className="SidebarPanel__SectionTitle">Draft Shapes</h3>
                        <div className="SidebarPanel__ShapesGrid">
                            {pagedDrafts.map((draft) => (
                                <div key={draft.id} className="SidebarPanel__ShapeThumbWrap">
                                    <button
                                        type="button"
                                        className="SidebarPanel__ShapeThumb"
                                        onClick={() => handleSelectDraft(draft)}
                                        aria-label="Draft shape"
                                    >
                                        {draft.previewUrl ? (
                                            <img
                                                src={draft.previewUrl}
                                                alt=""
                                                className="SidebarPanel__ShapeThumbImg"
                                            />
                                        ) : (
                                            <span className="SidebarPanel__ShapeThumbPlaceholder">Draft</span>
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        className="SidebarPanel__ShapeThumbDelete"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            setDeleteConfirm({type: 'draft', item: draft})
                                        }}
                                        aria-label="Delete draft"
                                    >
                                        <Icon icon={TrashIcon} width={14} height={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                        {draftPagination.totalPages > 1 && (
                            <div className="SidebarPanel__Pagination">
                                <button
                                    type="button"
                                    className="SidebarPanel__PaginationBtn"
                                    disabled={!draftPagination.canPrev}
                                    onClick={draftPagination.goPrev}
                                    aria-label="Previous draft page"
                                >
                                    Prev
                                </button>
                                <span className="SidebarPanel__PaginationLabel">
                                    {draftPagination.page} / {draftPagination.totalPages}
                                </span>
                                <button
                                    type="button"
                                    className="SidebarPanel__PaginationBtn"
                                    disabled={!draftPagination.canNext}
                                    onClick={draftPagination.goNext}
                                    aria-label="Next draft page"
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Saved Shapes block */}
                <div className="SidebarPanel__Section">
                    <h3 className="SidebarPanel__SectionTitle">Saved Shapes</h3>

                    {error && (
                        <p className="SidebarPanel__Error" role="alert">
                            {error.message}
                        </p>
                    )}

                    {loading && (
                        <div className="SidebarPanel__ShapesGrid">
                            {Array.from({length: limit}).map((_, i) => (
                                <div
                                    key={`skeleton-${i}`}
                                    className="SidebarPanel__ShapeThumb SidebarPanel__ShapeThumb--skeleton"
                                />
                            ))}
                        </div>
                    )}

                    {!loading && !error && (
                        <>
                            {items.length === 0 ? (
                                <p className="SidebarPanel__Empty">No shapes found.</p>
                            ) : (
                                <div className="SidebarPanel__ShapesGrid">
                                    {items.map((template) => (
                                        <div key={template._id} className="SidebarPanel__ShapeThumbWrap">
                                            <button
                                                type="button"
                                                className="SidebarPanel__ShapeThumb"
                                                onClick={() => handleSelectShape(template)}
                                                aria-label={template.name || 'Shape'}
                                            >
                                                {template.preview_image ? (
                                                    <img
                                                        src={template.preview_image}
                                                        alt=""
                                                        className="SidebarPanel__ShapeThumbImg"
                                                    />
                                                ) : (
                                                    <span className="SidebarPanel__ShapeThumbPlaceholder">
                                                        No preview
                                                    </span>
                                                )}
                                            </button>
                                            <button
                                                type="button"
                                                className="SidebarPanel__ShapeThumbDelete"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    setDeleteConfirm({type: 'saved', item: template})
                                                }}
                                                aria-label="Delete shape"
                                            >
                                                <Icon icon={TrashIcon} width={14} height={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {savedPagination.totalPages > 1 && (
                                <div className="SidebarPanel__Pagination">
                                    <button
                                        type="button"
                                        className="SidebarPanel__PaginationBtn"
                                        disabled={!savedPagination.canPrev}
                                        onClick={savedPagination.goPrev}
                                        aria-label="Previous page"
                                    >
                                        Prev
                                    </button>
                                    <span className="SidebarPanel__PaginationLabel">
                                        {savedPagination.page} / {savedPagination.totalPages}
                                    </span>
                                    <button
                                        type="button"
                                        className="SidebarPanel__PaginationBtn"
                                        disabled={!savedPagination.canNext}
                                        onClick={savedPagination.goNext}
                                        aria-label="Next page"
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {deleteConfirm && (
                <DeleteShapeConfirmModal
                    isOpen={!!deleteConfirm}
                    toggle={closeDeleteModal}
                    type={deleteConfirm.type}
                    itemName={deleteConfirm.type === 'saved' ? deleteConfirm.item.name : undefined}
                    onConfirm={handleDeleteConfirm}
                />
            )}
        </section>
    )
}

export default ShapesPanel

